import { gql } from '@apollo/client';
import type { AssetType } from '#/graphql/types';
import { categories, criticalityLevels, inUseStatuses } from '../components/AssetType.Mappings';
import { getUser } from '#/graphql/queries';
import FieldDefs from '#/types/FieldDefs';
import { getBaseColumns } from '#/common/utils/baseColumns';

const fields = {
  ...getBaseColumns(),
  id: {
    dataField: 'id',
    label: 'ID Number',
    editorType: 'TextBox',
    editorOptions: {
      readOnly: true,
    },
    isRequired: true,
  },
  name: {
    dataField: 'name',
    label: 'Name',
    isRequired: true,
    editorType: 'TextBox',
  },
  category: {
    dataField: 'category',
    label: 'Category',
    editorType: 'Lookup',
    editorOptions: {
      placeholder: 'Select Category',
      displayExpr: 'label',
      valueExpr: 'key',
      searchEnabled: false,
      dataSource: Object.values(categories),
    },
    isRequired: true,
  },
  defaultRiskScore: {
    dataField: 'defaultRiskScore',
    label: 'Default Risk Score',
    editorType: 'NumberBox',
    isRequired: true,
    editorOptions: {
      format: '0.#',
      placeholder: 'Enter a number',
      min: 0,
      max: 1,
      step: 0.1,
      showSpinButtons: true,
    },
  },

  description: {
    dataField: 'description',
    label: 'Description',
    editorType: 'TextArea',
    editorOptions: {
      placeholder: 'Enter description',
      height: 100,
      autoResizeEnabled: true,
    },
  },
  criticalityLevel: {
    dataField: 'criticalityLevel',
    label: 'Criticality Level',
    editorType: 'SelectBox',
    editorOptions: {
      placeholder: 'Select Criticality Level',
      displayExpr: 'label',
      valueExpr: 'value',
      searchEnabled: false,
      items: Object.values(criticalityLevels),
      showClearButton: true,
    },
  },
  expectedUsefulLife: {
    dataField: 'expectedUsefulLife',
    label: 'Expected Useful Life (years)',
    editorType: 'NumberBox',
    editorOptions: {
      format: '#0',
      min: 0,
      max: 1000,
      step: 1,
      placeholder: 'Enter a number',
      showSpinButtons: true,
    },
  },
  defaultWarrantyPeriod: {
    dataField: 'defaultWarrantyPeriod',
    label: 'Default Warranty Period',
    editorType: 'NumberBox',
    editorOptions: {
      format: '#0',
      min: 0,
      max: 1000,
      step: 1,
      placeholder: 'Enter a number',
      showSpinButtons: true,
    },
  },
  manufacturerRecommendations: {
    dataField: 'manufacturerRecommendations',
    label: "Manufacturer's Recommendations",
    editorType: 'TextArea',
  },
  image: {
    dataField: 'image',
    label: 'Image',
    editorType: 'TextBox',
    editorOptions: {
      placeholder: 'Images will be uploaded here',
      readOnly: true,
    },
    cssClass: 'hide-label',
  },
  inUseStatus: {
    dataField: 'inUseStatus',
    label: 'In Use Status',
    isRequired: true,
    editorType: 'Lookup',
    editorOptions: {
      displayExpr: 'label',
      valueExpr: 'key',
      searchEnabled: false,
      dataSource: Object.values(inUseStatuses),
    },
  },
  thirdPartyId: {
    dataField: 'thirdPartyId',
    label: 'Third Party ID',
    editorType: 'TextBox',
    editorOptions: {
      readOnly: true,
    },
  },
  thirdPartySource: {
    dataField: 'thirdPartySource',
    label: 'Third Party Source',
    editorType: 'TextBox',
    editorOptions: {
      readOnly: true,
    },
  },
} satisfies FieldDefs<AssetType>;

export default fields;
